(() => {
  const DESKTOP_MEDIA_QUERY = '(min-width: 990px)';
  const PAGE_WRAPPER_SELECTOR = '.page-wrapper';
  const PAGE_CONTENT_SELECTOR = '.page-wrapper__content';
  const SITE_LOADER_SELECTOR = '[data-load-wrap]';
  const LOADER_COMPLETE_EVENT = 'wambolt:loader:complete';

  const desktopQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
  const reducedMotionQuery = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );

  let lenis = null;
  let unsubscribeScrollTrigger = null;
  let refreshFrameOne = null;
  let refreshFrameTwo = null;
  let finalRefreshPending = false;


  /* =========================================
     SCROLLTRIGGER
     ========================================= */

  function hasScrollTrigger() {
    return Boolean(
      window.gsap &&
      window.ScrollTrigger
    );
  }

  function disconnectScrollTrigger() {
    if (
      typeof unsubscribeScrollTrigger ===
      'function'
    ) {
      unsubscribeScrollTrigger();
    }

    unsubscribeScrollTrigger = null;
  }

  function connectScrollTrigger() {
    if (
      !lenis ||
      !hasScrollTrigger()
    ) {
      return;
    }

    gsap.registerPlugin(ScrollTrigger);
    disconnectScrollTrigger();

    /*
     * Keep ScrollTrigger synchronized with the
     * active Lenis scroll position.
     */
    unsubscribeScrollTrigger =
      lenis.on('scroll', () => {
        ScrollTrigger.update();
      });
  }


  /* =========================================
     ACTIVE SCROLL SOURCE
     ========================================= */

  function getScrollElements() {
    const wrapper =
      document.querySelector(
        PAGE_WRAPPER_SELECTOR
      );

    const content =
      document.querySelector(
        PAGE_CONTENT_SELECTOR
      );

    const useDesktopWrapper = Boolean(
      desktopQuery.matches &&
      wrapper &&
      content
    );

    return {
      wrapper,
      content,
      useDesktopWrapper,
      scroller: useDesktopWrapper
        ? wrapper
        : window,
    };
  }


  /* =========================================
     REFRESH
     ========================================= */

  function cancelScheduledRefresh() {
    if (refreshFrameOne !== null) {
      window.cancelAnimationFrame(
        refreshFrameOne
      );

      refreshFrameOne = null;
    }

    if (refreshFrameTwo !== null) {
      window.cancelAnimationFrame(
        refreshFrameTwo
      );

      refreshFrameTwo = null;
    }
  }

  function scheduleScrollRefresh() {
    cancelScheduledRefresh();

    /*
     * Two animation frames intentionally allow
     * Shopify, responsive CSS, and browser layout
     * to settle before measurements are refreshed.
     */
    refreshFrameOne =
      window.requestAnimationFrame(() => {
        refreshFrameOne = null;

        refreshFrameTwo =
          window.requestAnimationFrame(() => {
            refreshFrameTwo = null;

            if (
              lenis &&
              typeof lenis.resize === 'function'
            ) {
              lenis.resize();
            }

            if (hasScrollTrigger()) {
              ScrollTrigger.refresh();
            }
          });
      });
  }


  function siteLoaderIsActive() {
    return Boolean(
      document.querySelector(
        SITE_LOADER_SELECTOR
      )
    );
  }


  function scheduleFinalLayoutRefresh() {
    if (siteLoaderIsActive()) {
      finalRefreshPending = true;
      return;
    }

    finalRefreshPending = false;
    scheduleScrollRefresh();
  }


  /* =========================================
     DESTROY
     ========================================= */

  function destroyLenis() {
    disconnectScrollTrigger();

    if (lenis) {
      lenis.destroy();
      lenis = null;
    }

    window.wamboltLenis = null;
  }


  /* =========================================
     INITIALIZE
     ========================================= */

  function initLenis() {
    destroyLenis();

    const {
      wrapper,
      content,
      useDesktopWrapper,
      scroller,
    } = getScrollElements();

    /*
     * Always expose the actual native scroll source,
     * even if Lenis cannot initialize.
     */
    window.wamboltScrollElement = scroller;

    if (typeof window.Lenis !== 'function') {
      console.warn(
        'Wambolt Lenis could not initialize because Lenis is unavailable.'
      );

      scheduleScrollRefresh();
      return;
    }

    const options = {
      autoRaf: true,
      smoothWheel: !reducedMotionQuery.matches,
    };

    /*
     * Preserve the established Wambolt scroll model:
     *
     * Desktop:
     *   .page-wrapper is the scroll container.
     *
     * Tablet/mobile:
     *   window is the scroll container.
     *
     * autoRaf remains enabled to preserve the existing
     * Lenis timing and component integration.
     */
    if (useDesktopWrapper) {
      lenis = new Lenis({
        ...options,
        wrapper,
        content,
      });
    } else {
      lenis = new Lenis(options);
    }

    window.wamboltLenis = lenis;

    connectScrollTrigger();

    /*
     * Notify components that the Lenis instance or
     * active scroll source may have changed.
     */
    window.dispatchEvent(
      new CustomEvent('wambolt:lenis:ready', {
        detail: {
          lenis,
          scroller,
        },
      })
    );

    scheduleScrollRefresh();
  }


  /* =========================================
     ENVIRONMENT CHANGES
     ========================================= */

  function handleScrollEnvironmentChange() {
    initLenis();
  }

  function addMediaQueryListener(
    query,
    handler
  ) {
    if (
      typeof query.addEventListener ===
      'function'
    ) {
      query.addEventListener(
        'change',
        handler
      );

      return;
    }

    if (
      typeof query.addListener ===
      'function'
    ) {
      query.addListener(handler);
    }
  }


  /* =========================================
     START
     ========================================= */

  initLenis();

  addMediaQueryListener(
    desktopQuery,
    handleScrollEnvironmentChange
  );

  addMediaQueryListener(
    reducedMotionQuery,
    handleScrollEnvironmentChange
  );


  /* =========================================
     FINAL LAYOUT REFRESH
     ========================================= */

  window.addEventListener(
    'load',
    () => {
      /*
       * ScrollTrigger may have been unavailable during
       * an unusual Shopify hot reload.
       */
      connectScrollTrigger();
      scheduleFinalLayoutRefresh();
    },
    {
      once: true,
    }
  );

  if (
    document.fonts &&
    document.fonts.ready
  ) {
    document.fonts.ready.then(() => {
      scheduleFinalLayoutRefresh();
    });
  }


  window.addEventListener(
    LOADER_COMPLETE_EVENT,
    () => {
      if (
        finalRefreshPending ||
        window.wamboltLenis
      ) {
        scheduleScrollRefresh();
      }

      finalRefreshPending = false;
    }
  );


  /* =========================================
     SHOPIFY THEME EDITOR
     ========================================= */

  document.addEventListener(
    'shopify:section:load',
    scheduleScrollRefresh
  );

  document.addEventListener(
    'shopify:section:unload',
    scheduleScrollRefresh
  );

  document.addEventListener(
    'shopify:section:reorder',
    scheduleScrollRefresh
  );
})();